/** System information, work settings and the email outbox. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { UnitsForm } from "./units.js";

export type NumberSequence = {
  key: string;
  prefix: string;
  includeYear: boolean;
  padding: number;
  nextValue: number;
};

export type SystemInfo = {
  appUrl?: string;
  latestMigration?: string | null;
  emails?: { pending: number; failed: number };
  appVersion: string;
  nodeEnvironment: string;
  storageBackend: string;
  postmarkConfigured: boolean;
};

export function SystemPanel() {
  const { t } = useTranslation();
  const system = useQuery({
    queryKey: ["settings", "system"],
    queryFn: () =>
      apiGet<{
        data: SystemInfo;
      }>("/settings/system").then((response) => response.data),
  });
  const numbering = useQuery({
    queryKey: ["numbering"],
    queryFn: () =>
      apiGet<{
        data: NumberSequence[];
      }>("/settings/numbering").then((response) => response.data),
  });
  if (system.isLoading) return <LoadingPanel />;
  if (system.error || !system.data) return <ErrorPanel error={system.error} />;
  return (
    <div className="space-y-5">
      <Section
        title={t("settings.system", {
          defaultValue: "System",
        })}
      >
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.version", {
                defaultValue: "Version",
              })}
            </dt>
            <dd>{system.data.appVersion}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.environment", {
                defaultValue: "Environment",
              })}
            </dt>
            <dd>{system.data.nodeEnvironment}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.storage", {
                defaultValue: "Storage",
              })}
            </dt>
            <dd>{system.data.storageBackend}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.migration")}
            </dt>
            <dd className="font-mono text-xs">
              {system.data.latestMigration ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">Postmark</dt>
            <dd>
              {system.data.postmarkConfigured
                ? t("common.configured", {
                    defaultValue: "Configured",
                  })
                : t("common.notConfigured", {
                    defaultValue: "Not configured",
                  })}
            </dd>
          </div>
        </dl>
      </Section>
      <EmailOutbox />
      <UnitsForm />
      <Section
        title={t("settings.numbering", {
          defaultValue: "Numbering",
        })}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {numbering.data?.map((sequence) => (
            <div key={sequence.key} className="rounded-lg border p-3 text-sm">
              <strong className="capitalize">
                {t(`settings.sequence.${sequence.key}`, {
                  defaultValue: sequence.key.replaceAll("_", " "),
                })}
              </strong>
              <span className="mt-1 block text-app-muted">
                {sequence.prefix}
                {sequence.includeYear ? `${new Date().getFullYear()}-` : ""}
                {String(sequence.nextValue).padStart(sequence.padding, "0")}
              </span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function WorkSettingsForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["settings", "work"],
    queryFn: () =>
      apiGet<{
        data: { defaultWorkdayMinutes: number; vacationWeeks: string };
      }>("/settings/work").then((response) => response.data),
  });
  const [form, setForm] = useState<{
    defaultWorkdayMinutes: number;
    vacationWeeks: string;
  } | null>(null);
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);
  const save = useMutation({
    mutationFn: () =>
      apiPatch("/settings/work", {
        defaultWorkdayMinutes: form!.defaultWorkdayMinutes,
        vacationWeeks: form!.vacationWeeks,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["settings", "work"] }),
  });
  if (!form) return <LoadingPanel />;
  return (
    <Section
      title={t("settings.workTime")}
      subtitle={t("settings.workTimeHint")}
    >
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label={t("settings.workdayHours")}>
          <InputNumber.Root
            fluid
            min={0.5}
            max={24}
            minFractionDigits={0}
            maxFractionDigits={2}
            value={form.defaultWorkdayMinutes / 60}
            onValueChange={(event: { value: number | null }) =>
              setForm({
                ...form,
                defaultWorkdayMinutes: Math.round((event.value ?? 0) * 60),
              })
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
        <Field label={t("settings.vacationWeeks")}>
          <InputNumber.Root
            fluid
            min={0}
            max={20}
            minFractionDigits={0}
            maxFractionDigits={2}
            value={Number(form.vacationWeeks)}
            onValueChange={(event: { value: number | null }) =>
              setForm({ ...form, vacationWeeks: String(event.value ?? 0) })
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
        <MutationError error={save.error} />
        <div className="sm:col-span-2">
          <Button type="submit" loading={save.isPending || undefined}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export type OutboxEmail = {
  id: string;
  status: "pending" | "processing" | "done" | "failed";
  to: string;
  subject: string;
  entityType: string | null;
  entityId: string | null;
  attemptCount: number;
  lastError: string | null;
  createdAt: string;
};

export function EmailOutbox() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const emails = useQuery({
    queryKey: ["settings", "emails"],
    queryFn: () =>
      apiGet<{ data: OutboxEmail[] }>("/settings/system/emails").then(
        (response) => response.data,
      ),
    refetchInterval: 30_000,
  });
  const retry = useMutation({
    mutationFn: (id: string) => apiPost(`/settings/system/emails/${id}/retry`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["settings", "emails"] }),
  });
  const rows = emails.data ?? [];
  return (
    <Section title={t("settings.emails")} subtitle={t("settings.emailsHint")}>
      {rows.length === 0 ? (
        <p className="text-sm text-app-muted">{t("settings.noEmails")}</p>
      ) : (
        <ul className="divide-y divide-app-border text-sm">
          {rows.slice(0, 30).map((email) => (
            <li
              key={email.id}
              className="flex items-start justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{email.subject}</div>
                <div className="truncate text-xs text-app-muted">
                  {email.to} · {new Date(email.createdAt).toLocaleString()}
                </div>
                {email.lastError ? (
                  <div className="text-xs text-danger">{email.lastError}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge
                  status={
                    email.status === "done"
                      ? "sent"
                      : email.status === "failed"
                        ? "failed"
                        : "pending"
                  }
                />
                {email.status === "failed" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    loading={
                      (retry.isPending && retry.variables === email.id) ||
                      undefined
                    }
                    onClick={() => retry.mutate(email.id)}
                  >
                    {t("settings.retry")}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      <MutationError error={retry.error} />
    </Section>
  );
}
